/* @flow */
import * as React from 'react';
import { themr, type ThemeType } from 'react-css-themr';
import { compose } from 'recompose';
import { translate, type TFunction } from 'react-i18next';
import { Text, Stack, Position, Flex, Box, Icon } from '@locus-taxy/neo-ui';
import { OutlineCheckbox } from 'common/components/core/form/Checkbox';
import {
    RadioGroup,
    RadioButton
} from 'common/components/core/form/RadioButton';
import _ from 'lodash';
import theme from './FpPlanMapOptions.scss';
import { SizeEnum } from 'common/enums/UIEnums';
import { PolygonOptionTypes } from 'common/entity/pjpMaster/planView/FpAllocatedVisitsContainer';
import { Containers } from 'common/enums/PlanFilterEnum';
import FpPlanViewLegends from './FpPlanViewLegends';
import FpMapBeatMetricBox from './FpMapBeatMetricBox';
import UnitConversionHelper from 'common/helpers/UnitConversionHelper';

class MapOptionsOutlineCheckbox extends React.PureComponent<$FlowFixMeProps> {
    onCheckboxChange = value => {
        this.props.onChange(this.props.mapOptionName, value);
    };
    render() {
        const { mapOptionName, ...others } = this.props; //eslint-disable-line no-unused-vars
        return <OutlineCheckbox {...others} onChange={this.onCheckboxChange} />;
    }
}
class MapOptionsRadioGroup extends React.PureComponent<$FlowFixMeProps> {
    onRadioGroupChange = value => {
        this.props.onChange(this.props.mapOptionName, value);
    };
    render() {
        return (
            <Box>
                <RadioGroup
                    {...this.props}
                    onChange={this.onRadioGroupChange}
                />
            </Box>
        );
    }
}

export const FpMapColorByOptions = Object.freeze({
    SalesCategory: 'salesCategory',
    Riders: 'riders',
    Week: 'week',
    WeekDay: 'weekDay'
});

type OwnProps = {
    theme: ThemeType,
    showMarkers: boolean,
    aerialRoute: boolean,
    clusters: boolean,
    polygon: boolean,
    showUnassignedMarkers: boolean,
    colorBy: boolean,
    colorByType: string,
    onChange: (
        options: Object,
        updatedOption: { id: string, value: string | boolean }
    ) => void
};

type Props = {
    ...OwnProps,
    theme: ThemeType,
    unallocatedVisitCount: number,
    legendMap: Map<string, Object>,
    getContainer: () => string,
    t: TFunction,
    UnitConverter: UnitConversionHelper,
    polygonOption: string
};
type State = {
    isOpen: boolean,
    options: {
        showUnassignedMarkers: boolean,
        drivingRoute: boolean,
        aerialRoute: boolean,
        polygon: boolean,
        clusters: boolean,
        showMarkerOptions: boolean,
        showZoneOptions: boolean,
        polygonClustersOption: boolean,
        allPolygons: boolean
    }
};
class FpPlanMapOptions extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            isOpen: false,
            isLegendsOpen: false,
            options: {
                showUnassignedMarkers: false,
                aerialRoute: true
            }
        };
        this.onToggleView = this.onToggleView.bind(this);
    }

    onValueChange = (id: string, value: string | boolean) => {
        let { ...options } = this.props;
        const { onChange } = this.props;
        options = _.extend({}, options);
        options[id] = value;
        onChange(options);
    };

    onToggleView = () => {
        if (this.state.isOpen) {
            this.hide();
        } else {
            this.show();
        }
    };
    show = () => {
        this.setState({ isOpen: true });
    };
    hide = () => {
        this.setState({ isOpen: false });
    };

    updateMapOptions = options => {
        this.setState(prevState => {
            const prevOptions = prevState.options;
            return {
                options: {
                    ...prevOptions,
                    ...options
                }
            };
        });
    };

    renderUnallocatedOption(unallocatedCount, showUnassignedMarkers) {
        const { t } = this.props;
        return (
            <Flex
                px={2}
                height={48}
                alignItems="center"
                justifyContent="space-between"
                title={t('fpPlanView.unallocatedTasks')}
            >
                <MapOptionsOutlineCheckbox
                    label={t('fpPlanView.unallocatedTasks')}
                    checked={showUnassignedMarkers}
                    onChange={this.onValueChange}
                    mapOptionName="showUnassignedMarkers"
                    theme={theme}
                />
                <Text color="white" pr={1}>
                    {unallocatedCount}
                </Text>
            </Flex>
        );
    }

    renderAerialRouteOption(aerialRoute) {
        const { t } = this.props;
        const { options } = this.state;
        if (!options.aerialRoute) {
            return null;
        }
        return (
            <MapOptionsOutlineCheckbox
                checked={aerialRoute}
                onChange={this.onValueChange}
                mapOptionName="aerialRoute"
                theme={theme}
                label={t('fpPlanView.aerialRoute').toUpperCase()}
            />
        );
    }

    renderPolygonOption(polygon) {
        const { t, polygonOption, getContainer } = this.props;
        const { options } = this.state;
        if (!options.polygon) {
            return null;
        }
        return (
            <Box>
                <MapOptionsOutlineCheckbox
                    checked={polygon}
                    onChange={this.onValueChange}
                    mapOptionName="polygon"
                    theme={theme}
                    label={t('fpPlanView.polygon').toUpperCase()}
                />
                <MapOptionsRadioGroup
                    onChange={this.onValueChange}
                    mapOptionName="polygonOption"
                    value={polygonOption}
                    theme={theme}
                >
                    <RadioButton
                        theme={theme}
                        label={t('fpPlanView.all')}
                        value={PolygonOptionTypes.ALL}
                        disabled={!polygon}
                        invert
                        size={SizeEnum.Large}
                    />
                    {getContainer() === Containers.Allocated ? (
                        <RadioButton
                            theme={theme}
                            label={t('fpPlanView.filtered')}
                            value={PolygonOptionTypes.FILTERED}
                            invert
                            size={SizeEnum.Large}
                        />
                    ) : (
                        <React.Fragment />
                    )}
                    {getContainer() === Containers.Allocated ? (
                        <RadioButton
                            theme={theme}
                            label={t('fpPlanView.selected')}
                            value={PolygonOptionTypes.SELECTED}
                            invert
                            size={SizeEnum.Large}
                        />
                    ) : (
                        <React.Fragment />
                    )}
                </MapOptionsRadioGroup>
            </Box>
        );
    }

    toggleLegendPopupState = isOpen => {
        this.setState({
            isLegendsOpen: isOpen
        });
    };

    renderClustersOption(clusters) {
        const { t } = this.props;
        const { options } = this.state;
        if (!options.clusters) {
            return null;
        }
        return (
            <MapOptionsOutlineCheckbox
                checked={clusters}
                onChange={this.onValueChange}
                mapOptionName="clusters"
                theme={theme}
                label={t('fpPlanView.clusters').toUpperCase()}
            />
        );
    }

    render() {
        const {
            theme,
            showMarkers,
            aerialRoute,
            polygon,
            colorBy,
            showUnassignedMarkers,
            unallocatedVisitCount,
            clusters,
            colorByType,
            legendMap,
            getContainer,
            filteredBeats,
            polygonOption,
            visibleBeats,
            beatSelectionMap,
            UnitConverter,
            t
        } = this.props;

        const { isOpen, isLegendsOpen } = this.state;

        return (
            <Position
                width="20rem"
                position="absolute"
                display="flex"
                right="1rem"
                sx={{
                    zIndex: 1,
                    transition: '0.5s ease all'
                }}
            >
                <Stack space="small">
                    <Box
                        bg="black"
                        p={1}
                        height={isOpen ? '34rem' : '10rem'}
                        sx={{
                            transition: '0.5s ease height',
                            marginTop: '1rem',
                            borderRadius: '4px'
                        }}
                    >
                        {this.renderUnallocatedOption(
                            unallocatedVisitCount,
                            showUnassignedMarkers
                        )}
                        <Flex
                            height={32}
                            alignItems="center"
                            px={2}
                            py={1}
                            onClick={this.onToggleView}
                            sx={{
                                cursor: 'pointer'
                            }}
                        >
                            <Box width={12}>
                                <Icon
                                    color="white"
                                    icon={
                                        isOpen
                                            ? 'arrow_drop_down'
                                            : 'arrow_drop_right'
                                    }
                                />
                            </Box>
                            <Box px={2}>
                                <Text fontSize={0} color="white">
                                    {t('fpPlanView.mapControls')}
                                </Text>
                            </Box>
                        </Flex>
                        <Box
                            p={1}
                            sx={{
                                visibility: isOpen ? 'visible' : 'hidden',
                                transition: '0.2s ease opacity',
                                opacity: isOpen ? 1 : 0
                            }}
                        >
                            <MapOptionsOutlineCheckbox
                                checked={showMarkers}
                                onChange={this.onValueChange}
                                mapOptionName="showMarkers"
                                theme={theme}
                                label={t('fpPlanView.showMarkers')}
                            />
                            {this.renderAerialRouteOption(aerialRoute)}
                            {this.renderPolygonOption(polygon)}
                            {this.renderClustersOption(clusters)}
                            <div className={theme.radioGroupWrap}>
                                <MapOptionsOutlineCheckbox
                                    checked={colorBy}
                                    onChange={this.onValueChange}
                                    mapOptionName="colorBy"
                                    theme={theme}
                                    label={t('fpPlanView.colorBy')}
                                />
                                <MapOptionsRadioGroup
                                    onChange={this.onValueChange}
                                    mapOptionName="colorByType"
                                    value={colorByType}
                                    theme={theme}
                                >
                                    <RadioButton
                                        theme={theme}
                                        label={t('fpPlanView.salesCategory')}
                                        value={
                                            FpMapColorByOptions.SalesCategory
                                        }
                                        disabled={!colorBy}
                                        invert
                                        size={SizeEnum.Large}
                                    />

                                    <RadioButton
                                        theme={theme}
                                        label={t('fpPlanView.riders')}
                                        value={FpMapColorByOptions.Riders}
                                        disabled={!colorBy}
                                        invert
                                        size={SizeEnum.Large}
                                    />
                                    <RadioButton
                                        theme={theme}
                                        label={t('fpPlanView.week')}
                                        value={FpMapColorByOptions.Week}
                                        disabled={!colorBy}
                                        invert
                                        size={SizeEnum.Large}
                                    />

                                    <RadioButton
                                        theme={theme}
                                        label={t('fpPlanView.weekDay')}
                                        value={FpMapColorByOptions.WeekDay}
                                        disabled={!colorBy}
                                        invert
                                        size={SizeEnum.Large}
                                    />
                                </MapOptionsRadioGroup>
                            </div>
                        </Box>
                    </Box>
                    <FpPlanViewLegends
                        getContainer={getContainer}
                        isMapOptionsOpen={isOpen}
                        legendMap={legendMap}
                        toggleLegendPopupState={this.toggleLegendPopupState}
                        t={t}
                    />
                    <FpMapBeatMetricBox
                        UnitConverter={UnitConverter}
                        getContainer={getContainer}
                        isMapOptionsOpen={isOpen}
                        isLegendsOpen={isLegendsOpen}
                        beatSelectionMap={beatSelectionMap}
                        polygonOption={polygonOption}
                        polygon={polygon}
                        filteredBeats={filteredBeats}
                        visibleBeats={visibleBeats}
                        t={t}
                    />
                </Stack>
            </Position>
        );
    }
}

export default (compose(
    themr('FpPlanMapOptions', theme, { withRef: true }),
    translate('entityManagement', { withRef: true })
)(FpPlanMapOptions): React.AbstractComponent<OwnProps>);
